import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { Search, ShieldOff, ShieldCheck, Wallet, Eye, Radio, IndianRupee } from "lucide-react";

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // Format: "10 Sep, 12:31 PM" — Asia/Kolkata (IST)
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function isOnlineNow(iso, mins = 5) {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) < mins * 60000;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [depositorsOnly, setDepositorsOnly] = useState(false);
  const [joinedFrom, setJoinedFrom] = useState("");
  const [joinedTo, setJoinedTo] = useState("");
  const [adjustingUser, setAdjustingUser] = useState(null);
  const [adjustAmt, setAdjustAmt] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/admin/users", { params: {
    q,
    only_online: onlineOnly,
    only_depositors: depositorsOnly,
    joined_from: joinedFrom || undefined,
    joined_to: joinedTo || undefined,
  } }).then(({data})=>setUsers(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, onlineOnly, depositorsOnly, joinedFrom, joinedTo]);
  // Auto-refresh every 15s when online filter is on
  useEffect(() => {
    if (!onlineOnly) return;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [onlineOnly, q]);

  const toggle = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/admin/users/${id}/toggle-block`); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const adjust = async () => {
    const amt = parseInt(adjustAmt);
    if (Number.isNaN(amt) || amt === 0) return toast.error("Enter non-zero amount (+credit or -debit)");
    try {
      await api.post("/admin/wallet/adjust", { user_id: adjustingUser.id, amount: amt, note: adjustNote });
      toast.success("Wallet updated");
      setAdjustingUser(null); setAdjustAmt(""); setAdjustNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onlineCount = users.filter(u => isOnlineNow(u.last_seen_at)).length;

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="flex items-start justify-between mb-4 sm:mb-6 gap-2 sm:gap-3 flex-wrap">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Management</div>
          <h1 className="font-display font-bold text-xl sm:text-3xl tracking-tight text-slate-900 mt-0.5 sm:mt-1">Users</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <button
            onClick={() => setOnlineOnly(v => !v)}
            data-testid="filter-online-toggle"
            className={`inline-flex items-center gap-1.5 px-2.5 h-9 sm:h-10 rounded-lg text-[10px] sm:text-xs font-black tracking-wider transition border-2 ${onlineOnly ? "bg-emerald-500 text-white border-emerald-600 shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${onlineOnly ? "bg-white animate-pulse" : "bg-emerald-500"}`} />
            <Radio className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {onlineOnly ? `ONLINE (${users.length})` : `Online Only`}
          </button>
          <button
            onClick={() => setDepositorsOnly(v => !v)}
            data-testid="filter-depositors-toggle"
            className={`inline-flex items-center gap-1.5 px-2.5 h-9 sm:h-10 rounded-lg text-[10px] sm:text-xs font-black tracking-wider transition border-2 ${depositorsOnly ? "bg-amber-500 text-white border-amber-600 shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-amber-400"}`}
          >
            <IndianRupee className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {depositorsOnly ? `DEPOSITED (${users.length})` : `Deposited Only`}
          </button>
          <div className="relative flex-1 sm:w-72 min-w-[160px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 sm:top-3 text-slate-400" />
            <Input data-testid="user-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name/mobile" className="pl-9 h-9 sm:h-10 text-sm" />
          </div>
        </div>
      </div>

      {/* Date filter */}
      <div className="flex items-center gap-2 flex-wrap mb-3 text-xs" data-testid="date-filter">
        <span className="text-slate-500 font-semibold">Joined:</span>
        <Input
          type="date"
          value={joinedFrom}
          onChange={(e) => setJoinedFrom(e.target.value)}
          data-testid="joined-from"
          className="h-8 text-xs w-auto flex-1 min-w-[130px] max-w-[160px]"
        />
        <span className="text-slate-400">→</span>
        <Input
          type="date"
          value={joinedTo}
          onChange={(e) => setJoinedTo(e.target.value)}
          data-testid="joined-to"
          className="h-8 text-xs w-auto flex-1 min-w-[130px] max-w-[160px]"
        />
        {(joinedFrom || joinedTo) && (
          <button
            onClick={() => { setJoinedFrom(""); setJoinedTo(""); }}
            className="text-rose-500 text-xs font-semibold hover:underline"
            data-testid="clear-date-filter"
          >Clear</button>
        )}
      </div>

      {!onlineOnly && (
        <div className="mb-4 flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-semibold border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {onlineCount} online right now
          </span>
          <span className="text-slate-400">· Auto-refresh every 15s when filter active</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Desktop / tablet: table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Mobile</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Last Seen</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.length === 0 && (
                <tr><td colSpan="7" className="text-center text-slate-400 py-10 text-sm">No users found</td></tr>
              )}
              {users.map(u => {
                const online = isOnlineNow(u.last_seen_at);
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-orange-50/30 cursor-pointer transition" data-testid={`user-row-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${online ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)] animate-pulse" : "bg-slate-300"}`} />
                        <span>{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-600">{u.mobile}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs tabular-nums">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {online ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online now</span>
                      ) : u.last_seen_at ? (
                        <div className="flex flex-col">
                          <span className="text-slate-700 font-semibold tabular-nums">{formatDateTime(u.last_seen_at)}</span>
                          <span className="text-[10px] text-slate-400">{timeAgo(u.last_seen_at)}</span>
                        </div>
                      ) : (<span className="text-slate-400 italic">Never opened</span>)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{u.wallet_balance}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-md ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{u.status}</span></td>
                    <td className="px-4 py-3 text-right" onClick={(e)=>e.stopPropagation()}>
                      <div className="inline-flex gap-2">
                        <Link to={`/admin/users/${u.id}`} data-testid={`view-${u.id}`} className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 rounded-md"><Eye className="w-3 h-3" /> View</Link>
                        <button data-testid={`adjust-${u.id}`} onClick={()=>setAdjustingUser(u)} className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 rounded-md"><Wallet className="w-3 h-3" /> Adjust</button>
                        <button data-testid={`block-${u.id}`} onClick={(e)=>toggle(u.id, e)} className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border ${u.status === "active" ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}>
                          {u.status === "active" ? (<><ShieldOff className="w-3 h-3" /> Block</>) : (<><ShieldCheck className="w-3 h-3" /> Unblock</>)}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile: card list */}
        <div className="sm:hidden divide-y divide-slate-100" data-testid="users-mobile-list">
          {users.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">No users found</div>
          )}
          {users.map(u => {
            const online = isOnlineNow(u.last_seen_at);
            return (
              <div key={u.id} className="p-3 active:bg-slate-50" data-testid={`user-card-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}`)}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm" style={{ background: online ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#64748b,#475569)" }}>
                    {(u.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-slate-900 truncate">{u.name}</div>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{u.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                      <span className="tabular-nums">📱 {u.mobile}</span>
                      {online ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />ONLINE</span>
                      ) : (
                        <span className="text-slate-400">{timeAgo(u.last_seen_at)}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Joined {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-emerald-700 font-bold text-sm tabular-nums">₹ {u.wallet_balance}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-2.5 grid grid-cols-3 gap-1.5" onClick={(e)=>e.stopPropagation()}>
                  <Link to={`/admin/users/${u.id}`} data-testid={`view-card-${u.id}`} className="flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg bg-slate-100 text-slate-700 active:bg-slate-200"><Eye className="w-3 h-3" /> View</Link>
                  <button data-testid={`adjust-card-${u.id}`} onClick={()=>setAdjustingUser(u)} className="flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg bg-blue-50 text-blue-700 active:bg-blue-100"><Wallet className="w-3 h-3" /> Wallet</button>
                  <button data-testid={`block-card-${u.id}`} onClick={(e)=>toggle(u.id, e)} className={`flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg ${u.status === "active" ? "bg-rose-50 text-rose-700 active:bg-rose-100" : "bg-emerald-50 text-emerald-700 active:bg-emerald-100"}`}>
                    {u.status === "active" ? (<><ShieldOff className="w-3 h-3" /> Block</>) : (<><ShieldCheck className="w-3 h-3" /> Unblock</>)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!adjustingUser} onOpenChange={(o)=>{if(!o)setAdjustingUser(null);}}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Adjust Wallet — {adjustingUser?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Current balance: <span className="tabular-nums font-semibold text-slate-900">{adjustingUser?.wallet_balance}</span></div>
          <Input data-testid="adjust-amount" placeholder="Amount (+credit or -debit)" value={adjustAmt} onChange={(e)=>setAdjustAmt(e.target.value)} className="mb-2" />
          <Input data-testid="adjust-note" placeholder="Note (optional)" value={adjustNote} onChange={(e)=>setAdjustNote(e.target.value)} className="mb-2" />
          <Button data-testid="adjust-submit" onClick={adjust} className="w-full btn-brand h-10">Apply</Button>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
